import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {takeUntilDestroy} from "take-until-destroy";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {PatientAccountService} from "../../../services/patient.account.service";
import {
  ChangePasswordResult,
  CreateAccountResult,
  IChangePasswordRequest,
  ICreateAccountRequest
} from "../common/common.types";
import {LogManager} from "../../../../common/log/log.manager";
import {LogCat} from "../../../../common/log/log.categories";
import {SetPasswordComponent} from "../../../../common/components/set.password/set.password.component";

@Component({
  selector: 'app-patient-account',
  templateUrl: './patient.account.component.html',
  styleUrls: ['./patient.account.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PatientAccountComponent implements OnInit, OnDestroy, AfterViewInit {
  @Output() back = new EventEmitter();
  @Output() accountCreated = new EventEmitter();
  @Output() passwordChanged = new EventEmitter();
  @Output() error = new EventEmitter();
  @ViewChild("PasswordForm", {static: true}) passwordForm: SetPasswordComponent;

  public formControls: FormGroup;
  public patient: any;
  public userName: string;
  public newPatient: boolean;
  public isUserNameReadOnly: boolean;
  public isUserNameInUse: boolean;
  public isCreateAccount = false;
  public isChangePassword = false;
  public oneTimeCode: string;
  public inProgress = false;

  constructor(private patientAccountSvc: PatientAccountService,
              private changeDetector: ChangeDetectorRef) {
  }

  ngOnInit() {
    this.formControls = new FormGroup({
      userName: new FormControl('', {validators: [Validators.required]})
    });
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.formControls.addControl("password", this.passwordForm.formControls);
      this.changeDetector.markForCheck();
    });
  }

  ngOnDestroy() {
  }

  public setPatientForAccountCreation(patient: any, newPatient: boolean, oneTimeCode: string) {
    this.patient = patient;
    this.newPatient = newPatient;
    this.isCreateAccount = true;
    this.isChangePassword = false;
    this.oneTimeCode = oneTimeCode;

    this.patientAccountSvc.generatePatientUserName(patient)
      .pipe(takeUntilDestroy(this))
      .subscribe(userName => {
          if (userName && userName !== '') {
            this.userName = userName;
            this.isUserNameReadOnly = true;
            this.passwordForm.password.nativeElement.focus();
            this.changeDetector.markForCheck();
          } else {
            this.changeDetector.markForCheck();
          }
        },
        err => {
          this.changeDetector.markForCheck();
          LogManager.error(LogCat.Login.CreatePatientAccount, err);
        });
  }

  public setPatientForPasswordChanging(patient: any, oneTimeCode: string) {
    this.patient = patient;
    this.newPatient = false;
    this.isCreateAccount = false;
    this.isChangePassword = true;
    this.oneTimeCode = oneTimeCode;

    this.patientAccountSvc.getPatientUserName(patient)
      .pipe(takeUntilDestroy(this))
      .subscribe(userName => {
          if (userName && userName !== '') {
            this.userName = userName;
            this.isUserNameReadOnly = true;
            this.passwordForm.password.nativeElement.focus();
            this.changeDetector.markForCheck();
          } else {
            LogManager.error(LogCat.Login.ForgotPassword, 'UserName was not found');
            this.changeDetector.markForCheck();
            this.error.emit();
          }
        },
        err => {
          this.changeDetector.markForCheck();
          LogManager.error(LogCat.Login.CreatePatientAccount, err);
        });
  }

  public createAccount() {
    this.isUserNameInUse = false;
    if (this.isValid()) {
      this.inProgress = true;
      const accountDetails: ICreateAccountRequest = {
        newPatient: this.newPatient,
        clinicalRequest: this.patient,
        userName: this.formControls.controls['userName'].value,
        password: btoa(this.passwordForm.formControls.controls['password'].value),
        oneTimeCode: this.oneTimeCode
      };
      this.patientAccountSvc.createPatientAccount(accountDetails)
        .pipe(takeUntilDestroy(this))
        .subscribe(res => {
            this.inProgress = false;
            if (res.result !== CreateAccountResult.AccountCreatedSuccessfully) {
              LogManager.error(LogCat.Login.CreatePatientAccount, 'Create patient account failed: ' + res.result);
              if (res.result === CreateAccountResult.UserNameAlreadyInUse) {
                this.isUserNameInUse = true;
                this.changeDetector.markForCheck();
                return;
              }
            }
            this.accountCreated.emit({patient: this.patient, createAccountResponse: res} );
          },
          err => {
            LogManager.error(LogCat.Login.CreatePatientAccount, err);
            this.inProgress = false;
            this.error.emit(this.patient);
          });
    }
    this.changeDetector.markForCheck();
  }

  public changePassword() {
    if (this.isValid()) {
      this.inProgress = true;
      const userCredentials: IChangePasswordRequest = {
        oneTimeCode: this.oneTimeCode,
        userName: this.formControls.controls['userName'].value,
        password: btoa(this.passwordForm.formControls.controls['password'].value)
      };
      const passwordForm = this.passwordForm;
      this.patientAccountSvc.changePassword(userCredentials)
        .pipe(takeUntilDestroy(this))
        .subscribe(res => {
            this.inProgress = false;
            if (res.result !== ChangePasswordResult.PasswordChangedSuccessfully) {
              LogManager.error(LogCat.Login.ForgotPassword, 'Password change failed: ' + res.result);
              if (res.result === ChangePasswordResult.NewPasswordRecentlyUsed) {
                passwordForm.setPasswordRecentlyUsed();
                this.changeDetector.markForCheck();
                return;
              }
            }
            this.passwordChanged.emit({patient: this.patient, changePasswordResponse: res});
          },
          err => {
            this.inProgress = false;
            LogManager.error(LogCat.Login.ForgotPassword, err);
            this.error.emit(this.patient);
          });
    }
    this.changeDetector.markForCheck();
  }

  public goback() {
    this.back.emit(this.patient);
  }

  public reset() {
    this.isUserNameInUse = false;
    this.formControls.reset();
    this.isCreateAccount = false;
    this.isChangePassword = false;
    this.userName = '';
  }

  public resetErrors() {
    this.isUserNameInUse = false;
    this.changeDetector.markForCheck();
  }

  private isValid(): boolean {
    if (this.formControls.controls['userName'].invalid) {
      return false;
    }
    return this.passwordForm.isValid();
  }
}
